import { useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { ensure, mapToNamedProperty, validationSuccessful } from "sprint-common-web";

import { IntegratorsDataContext } from "../../../../../common/data/lookup-data-providers/IntegratorsDataProvider";
import { System } from "../../../../../common/types/System";
import { SystemsDataContext } from "../../../../../orders/common/data-providers/SystemsDataProvider";
import { useAdminAirlineApi } from "../../../../airline/common/api/useAdminAirlineApi";
import { SystemPlatformListingContext } from "../../context/SystemPlatformListingContext";
import { SystemPlatformListingItem } from "../../SystemPlatformListingItem";
import { AddSystemPlatformFormInputs } from "./AddSystemPlatformFormInputs";

export const useAddSystemPlatformForm = () => {
    const { addSystemPlatform, validateSystem } = useAdminAirlineApi();

    const { systemPlatforms, createSystemPlatform: createSystemPlatformItem } = useContext(
        SystemPlatformListingContext
    );
    const { systems } = useContext(SystemsDataContext);
    const { integrators } = useContext(IntegratorsDataContext);

    const { airlineId } = useParams<{ airlineId: string }>();

    const [systemOptions, setSystemOptions] = useState<System[]>([]);

    const resetSystemOptions = (integratorId: string) => {
        const matchingSystems = getAvailableSystemsByIntegratorId(integratorId, systems, systemPlatforms);
        setSystemOptions(matchingSystems);
    };

    const validateUniqueSystem = async (systemId: string | undefined) => {
        if (!systemId) {
            return validationSuccessful();
        }

        return validateSystem({ airlineId, systemId });
    };

    const createSystemPlatform = async ({ integratorId, systemId }: AddSystemPlatformFormInputs): Promise<void> => {
        await addSystemPlatform({
            airlineId,
            integratorId,
            systemId
        });

        const system = systems.find((item) => item.id === systemId);
        const integrator = integrators.find((item) => item.id === integratorId);

        ensure(system);
        ensure(integrator);

        createSystemPlatformItem({
            systemId: system.id,
            systemName: system.name,
            integrator: mapToNamedProperty(integrator),
            isModified: true
        });
    };

    return {
        integratorOptions: integrators,
        systemOptions,
        resetSystemOptions,
        createSystemPlatform,
        validateUniqueSystem
    };
};

const getAvailableSystemsByIntegratorId = (
    integratorId: string,
    systems: System[],
    selectedSystems: SystemPlatformListingItem[]
): System[] =>
    systems.filter((system) => system.integrator.id === integratorId && !isAlreadySelected(system, selectedSystems));

const isAlreadySelected = (system: System, selectedSystemPlatforms: SystemPlatformListingItem[]): boolean =>
    selectedSystemPlatforms.map((item) => item.systemId).includes(system.id);
